import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Clock,
  Flag,
  MessageSquare,
  Shield,
  User,
  XCircle,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import VisitFullReport from '../../components/shared/VisitFullReport';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  formatWatDateTime,
  getChecklistValue,
  getLatestReview,
  getReviewHistory,
  getStatusClasses,
  getVisitDependantName,
  getVisitStart,
  normalizeVisitStatus,
  sortVisitsNewestFirst,
} from '../../lib/visitUtils';

const filters = [
  { value: 'all', label: 'All visits' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Submitted', label: 'Pending review' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Flagged', label: 'Flagged' },
  { value: 'Rejected', label: 'Rejected' },
];

const StatCard = ({ label, value, tone = 'burgundy' }) => {
  const toneClass = {
    burgundy: 'text-[#9F1D35]',
    green: 'text-emerald-700',
    red: 'text-[#A33232]',
    amber: 'text-amber-700',
  }[tone];
  return (
    <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
      <p className={`font-serif text-4xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
      <p className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{label}</p>
    </div>
  );
};

const sameWeek = (date, now = new Date()) => {
  if (!date) return false;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return date >= start && date < end;
};

const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const { visits, currentUser, anomalies, reviewVisit } = useApp();
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [reviewComment, setReviewComment] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const allVisits = useMemo(() => sortVisitsNewestFirst(visits), [visits]);
  const selectedVisit = allVisits.find((visit) => visit.id === selectedVisitId) || allVisits[0] || null;
  const filteredVisits = allVisits.filter((visit) => activeFilter === 'all' || normalizeVisitStatus(visit.status) === activeFilter);
  const visitsThisWeek = allVisits.filter((visit) => sameWeek(getVisitStart(visit))).length;
  const visitsMade = allVisits.filter((visit) => ['Submitted', 'Approved', 'Flagged', 'Rejected'].includes(normalizeVisitStatus(visit.status))).length;
  const flaggedCount = allVisits.filter((visit) => normalizeVisitStatus(visit.status) === 'Flagged').length;
  const anomalyCount = anomalies?.filter((item) => item.anomalyFlags?.some((flag) => flag.status === 'Under Review'))?.length ?? 0;

  const pendingReviewCount = allVisits.filter((visit) => normalizeVisitStatus(visit.status) === 'Submitted').length;
  const latestReview = getLatestReview(selectedVisit);
  const history = getReviewHistory(selectedVisit);
  const officerResponse = selectedVisit?.latestOfficerResponse;
  const selectedStatus = normalizeVisitStatus(selectedVisit?.status);
  const selectedFinalized = ['Approved', 'Rejected'].includes(selectedStatus);
  const selectedReviewable = ['Submitted', 'Flagged'].includes(selectedStatus);
  const handleSelectVisit = (visit) => {
    setSelectedVisitId(visit.id);
    setReviewComment('');
    setReviewError('');
  };

  const handleReview = async (status) => {
    if (!selectedVisit) return;
    if ((status === 'Flagged' || status === 'Rejected') && !reviewComment.trim()) {
      setReviewError('Add a review comment before flagging or rejecting this visit.');
      return;
    }
    setReviewing(true);
    setReviewError('');
    try {
      const updated = await reviewVisit(selectedVisit.id, status, reviewComment);
      setSelectedVisitId(updated.id);
      setReviewComment('');
    } catch (error) {
      setReviewError(getApiErrorMessage(error, 'Unable to save review action.'));
    } finally {
      setReviewing(false);
    }
  };

  const greetingHour = new Date().getHours();
  const greeting = greetingHour < 12 ? 'Good morning' : greetingHour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="supervisor-dashboard">
      <div className="border-b border-[#E8E6E3] bg-[#FAFAF7]">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#9F1D35] text-lg font-semibold text-[#FAFAF7]">
                {currentUser?.firstName?.[0] || 'S'}{currentUser?.lastName?.[0] || ''}
              </div>
              <div>
                <p className="text-sm text-[#5A5A5A]">{greeting},</p>
                <h1 className="font-serif text-2xl font-semibold text-[#1A1A1A] sm:text-3xl">
                  {`${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || 'Supervisor'}
                </h1>
                <p className="mt-1 flex items-center gap-2 text-sm text-[#5A5A5A]">
                  <Shield className="h-4 w-4" />
                  Operations and quality review
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/scheduling')}
                className="inline-flex items-center gap-2 rounded-full bg-[#9F1D35] px-4 py-2 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
                data-testid="schedule-visits-btn"
              >
                <Calendar className="h-4 w-4" />
                Schedule Visits
              </button>
              <button
                type="button"
                onClick={() => navigate('/reviews')}
                className="inline-flex items-center gap-2 rounded-full border border-[#9F1D35] px-4 py-2 text-sm font-medium text-[#9F1D35]"
              >
                <ClipboardList className="h-4 w-4" />
                Reviews
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
        <section className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Visits this week" value={visitsThisWeek} />
          <StatCard label="Visits made" value={visitsMade} tone="green" />
          <StatCard label="Flagged visits" value={flaggedCount} tone="red" />
          <StatCard label="Anomalies" value={anomalyCount} tone="amber" />
        </section>

        <section className="mb-5 flex flex-wrap gap-2">
          {filters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setActiveFilter(filter.value)}
              className={`rounded-full border px-4 py-2 text-sm font-medium ${
                activeFilter === filter.value
                  ? 'border-[#9F1D35] bg-[#9F1D35] text-[#FAFAF7]'
                  : 'border-[#D8D5D0] bg-[#FAFAF7] text-[#5A5A5A]'
              }`}
            >
              {filter.label}
            </button>
          ))}
          {pendingReviewCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800">
              {pendingReviewCount} pending
            </span>
          )}
        </section>

        <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
          <section className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
            <div className="border-b border-[#E8E6E3] px-5 py-4">
              <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">All Visits</h2>
              <p className="text-sm text-[#5A5A5A]">{filteredVisits.length} visible visits</p>
            </div>
            <div className="max-h-[720px] overflow-y-auto">
              {filteredVisits.map((visit) => (
                <button
                  key={visit.id}
                  type="button"
                  onClick={() => handleSelectVisit(visit)}
                  className={`block w-full border-b border-[#E8E6E3] p-4 text-left transition-colors hover:bg-[#F5E8EA]/50 ${
                    selectedVisit?.id === visit.id ? 'bg-[#F5E8EA]' : ''
                  }`}
                  data-testid={`review-item-${visit.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-[#1A1A1A]">{getVisitDependantName(visit)}</p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-[#5A5A5A]">
                        <Calendar className="h-3 w-3" />
                        {formatWatDateTime(getVisitStart(visit))}
                      </p>
                      <p className="mt-2 text-xs text-[#5A5A5A]">{visit.careOfficerName || visit.careOfficerSnapshot?.name || 'Care officer'}</p>
                    </div>
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusClasses(visit.status)}`}>{normalizeVisitStatus(visit.status)}</span>
                  </div>
                </button>
              ))}
              {filteredVisits.length === 0 && (
                <div className="p-8 text-center text-sm text-[#5A5A5A]">No visits match this filter.</div>
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
            {selectedVisit ? (
              <>
                <div className="border-b border-[#E8E6E3] p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">{getVisitDependantName(selectedVisit)}</h2>
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-[#5A5A5A]">
                        <Calendar className="h-4 w-4" />
                        {formatWatDateTime(getVisitStart(selectedVisit))}
                        {selectedVisit.duration ? (
                          <>
                            <span>·</span>
                            <Clock className="h-4 w-4" />
                            {selectedVisit.duration} minutes
                          </>
                        ) : null}
                      </p>
                      <p className="mt-2 flex items-center gap-2 text-sm text-[#5A5A5A]">
                        <User className="h-4 w-4" />
                        {selectedVisit.careOfficerName || selectedVisit.careOfficerSnapshot?.name || 'Care officer'}
                      </p>
                    </div>
                    <span className={`w-fit rounded-full border px-3 py-1 text-sm font-medium ${getStatusClasses(selectedVisit.status)}`}>{normalizeVisitStatus(selectedVisit.status)}</span>
                  </div>
                </div>

                <div className="max-h-[680px] space-y-5 overflow-y-auto p-5">
                  <VisitFullReport visit={selectedVisit} />
                  <div className="border-t border-[#E8E6E3] pt-5">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-[#E8E6E3] p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Wellness</p>
                      <p className="mt-2 font-serif text-3xl font-semibold text-[#9F1D35]">{selectedVisit.overallWellnessScore || '-'}</p>
                    </div>
                    <div className="rounded-2xl border border-[#E8E6E3] p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Temperature</p>
                      <p className="mt-2 text-sm font-medium text-[#1A1A1A]">{getChecklistValue(selectedVisit, ['vitals', 'bodyTemperature', 'value'])}°C</p>
                    </div>
                    <div className="rounded-2xl border border-[#E8E6E3] p-4">
                      <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Blood Pressure</p>
                      <p className="mt-2 text-sm font-medium text-[#1A1A1A]">
                        {getChecklistValue(selectedVisit, ['vitals', 'bloodPressure', 'systolic'])}/{getChecklistValue(selectedVisit, ['vitals', 'bloodPressure', 'diastolic'])}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-2xl border border-[#E8E6E3] p-4">
                      <p className="text-sm font-medium text-[#1A1A1A]">Care officer observations</p>
                      <p className="mt-2 text-sm text-[#5A5A5A]">{getChecklistValue(selectedVisit, ['otherObservations'])}</p>
                    </div>
                    <div className="rounded-2xl border border-[#E8E6E3] p-4">
                      <p className="text-sm font-medium text-[#1A1A1A]">General comments</p>
                      <p className="mt-2 text-sm text-[#5A5A5A]">{getChecklistValue(selectedVisit, ['generalComments'])}</p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#E8E6E3] p-4">
                    <p className="mb-3 flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
                      <MessageSquare className="h-4 w-4 text-[#9F1D35]" />
                      Review thread
                    </p>
                    <div className="space-y-3">
                      {latestReview && (
                        <div className="rounded-xl bg-[#F7F4EE] p-3">
                          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Latest supervisor review</p>
                          <p className="mt-1 text-sm font-medium text-[#1A1A1A]">{latestReview.status}</p>
                          {latestReview.comments && <p className="mt-2 text-sm text-[#5A5A5A]">{latestReview.comments}</p>}
                        </div>
                      )}
                      {officerResponse && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                          <p className="text-xs font-medium uppercase tracking-[0.12em] text-amber-800">Care officer response</p>
                          <p className="mt-2 text-sm text-amber-900">{officerResponse.comments}</p>
                        </div>
                      )}
                      {history.length === 0 && !latestReview && <p className="text-sm text-[#5A5A5A]">No review history yet.</p>}
                    </div>
                  </div>
                </div>
                </div>

                <div className="border-t border-[#E8E6E3] p-5">
                  {selectedReviewable ? (
                    <>
                      <textarea
                        placeholder="Add review comments..."
                        value={reviewComment}
                        onChange={(event) => setReviewComment(event.target.value)}
                        className="w-full rounded-2xl border border-[#D8D5D0] bg-[#FAFAF7] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                        rows={3}
                      />
                      {reviewError && <p className="mt-2 text-sm text-red-600">{reviewError}</p>}
                      <div className="mt-3 flex flex-wrap gap-3">
                        <button
                          type="button"
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-3 text-sm font-medium text-white disabled:opacity-60"
                          disabled={reviewing}
                          onClick={() => handleReview('Approved')}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Approve
                        </button>
                        <button
                          type="button"
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-amber-300 px-4 py-3 text-sm font-medium text-amber-800 disabled:opacity-60"
                          disabled={reviewing}
                          onClick={() => handleReview('Flagged')}
                        >
                          <Flag className="h-4 w-4" />
                          Keep Flagged
                        </button>
                        <button
                          type="button"
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-red-300 px-4 py-3 text-sm font-medium text-red-700 disabled:opacity-60"
                          disabled={reviewing}
                          onClick={() => handleReview('Rejected')}
                        >
                          <XCircle className="h-4 w-4" />
                          Reject
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4 text-sm text-[#5A5A5A]">
                      {selectedFinalized ? 'This visit is finalized. No further supervisor action can be taken.' : 'Scheduled visits cannot be reviewed until submitted.'}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="flex min-h-[520px] flex-col items-center justify-center p-8 text-center">
                <ClipboardList className="mb-4 h-16 w-16 text-[#D8D5D0]" />
                <h3 className="text-lg font-semibold text-[#1A1A1A]">Select a visit</h3>
                <p className="mt-1 text-sm text-[#5A5A5A]">All visits remain visible here after review.</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default SupervisorDashboard;
